// components/Teams/interview/InterviewVideoCall.jsx
import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback
} from "react";

import axios from "axios";
import io from "socket.io-client";

import {
  LiveKitRoom,
  RoomAudioRenderer,
  useTracks,
  useLocalParticipant,
  useSpeakingParticipants,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";

import config from "../../../config";
import { toast } from "../../../components/Common/Toast";

import { Camera } from "@mediapipe/camera_utils";
import { FaceMesh } from "@mediapipe/face_mesh";

import {
  FaWindowMaximize,
  FaWindowMinimize,
  FaMicrophone,
  FaMicrophoneSlash,
  FaVideo,
  FaVideoSlash,
  FaTimes,
  FaUserCheck,
  FaUserClock,
  FaCheckCircle,
  FaSpinner,
  FaExpand,
  FaCompress,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
} from "react-icons/fa";

import { MdCallEnd } from "react-icons/md";
import { HiOutlineClipboardList } from "react-icons/hi";

import "./InterviewVideoCall.css";

const getCurrentMinute = () => Math.floor(Date.now() / 1000 / 60) * 60;

// Participant Tile with Maximize Button
const ParticipantTileWithControls = ({ track, isMainSpeaker, onMaximize, isMaximized }) => {
  // FIX: Use track.participant.name instead of identity
  const participantName = track.participant.name || track.participant.identity || "Unknown";
  const isLocal = track.participant.isLocal;

  return (
    <div className={`intv-participant-tile-wrapper ${isMainSpeaker ? 'main-speaker' : ''} ${isMaximized ? 'maximized' : ''}`}>
      <div className="intv-participant-video">
        {track.publication?.kind === 'video' ? (
          <video
            ref={el => {
              if (el && track.publication?.track) {
                track.publication.track.attach(el);
              }
            }}
            autoPlay
            playsInline
            muted={isLocal}
            className="intv-video-element"
          />
        ) : (
          <div className="intv-no-video-placeholder">
            <FaUser />
          </div>
        )}
      </div>

      {/* Maximize button */}
      {!isLocal && (
        <button
          className="intv-maximize-btn"
          onClick={() => onMaximize(track)}
          title={isMaximized ? "Restore" : "Maximize"}
        >
          {isMaximized ? <FaCompress /> : <FaExpand />}
        </button>
      )}

      {/* Participant name overlay */}
      <div className="intv-participant-name-overlay">
        <span className="intv-participant-name-text">
          {isLocal ? 'You' : participantName}
        </span>
        {track.participant.isSpeaking && (
          <span className="intv-speaking-indicator-dot" />
        )}
      </div>
    </div>
  );
};

// Enhanced Video Layout Component
const EnhancedVideoLayout = ({ onMaximize, maximizedTrack, participants }) => {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  const speakingParticipants = useSpeakingParticipants();
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [showParticipantsPanel, setShowParticipantsPanel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PARTICIPANTS_PER_PAGE = 9;
  const MAX_SIDEBAR_TILES = 3;

  // Determine active speaker
  useEffect(() => {
    if (!maximizedTrack && speakingParticipants.length > 0) {
      const speaker = tracks.find(t => t.participant.sid === speakingParticipants[0]?.sid);
      if (speaker) {
        setActiveSpeaker(speaker);
      }
    }
  }, [speakingParticipants, tracks, maximizedTrack]);

  // Filter out screen share tracks from main view
  const cameraTracks = tracks.filter(t => t.source === Track.Source.Camera);
  const screenShareTrack = tracks.find(t => t.source === Track.Source.ScreenShare);

  // Determine main view track
  const mainTrack = maximizedTrack || activeSpeaker || cameraTracks[0];

  // Get sidebar tracks (excluding main track)
  const sidebarTracks = cameraTracks
    .filter(t => t.participant.sid !== mainTrack?.participant.sid)
    .slice(0, MAX_SIDEBAR_TILES);

  // Calculate remaining participants
  const remainingCount = Math.max(0, cameraTracks.length - MAX_SIDEBAR_TILES - (mainTrack ? 1 : 0));

  // Get remaining tracks for the participants panel
  const remainingTracks = cameraTracks.filter(t =>
    t.participant.sid !== mainTrack?.participant.sid &&
    !sidebarTracks.find(st => st.participant.sid === t.participant.sid)
  );

  // Calculate pagination
  const totalPages = Math.ceil(remainingTracks.length / PARTICIPANTS_PER_PAGE);
  const paginatedTracks = remainingTracks.slice(
    (currentPage - 1) * PARTICIPANTS_PER_PAGE,
    currentPage * PARTICIPANTS_PER_PAGE
  );

  // FIX: Only show sidebar when there are sidebar tracks OR remaining participants
  const showSidebar = sidebarTracks.length > 0 || remainingCount > 0;

  return (
    <div className="intv-enhanced-layout">
      {/* Main Content Area */}
      <div className="intv-main-content">
        {/* Screen Share */}
        {screenShareTrack && (
          <div className="intv-screen-share-area">
            <ParticipantTileWithControls
              track={screenShareTrack}
              isMainSpeaker={false}
              onMaximize={onMaximize}
              isMaximized={maximizedTrack?.participant.sid === screenShareTrack.participant.sid}
            />
          </div>
        )}

        {/* Main Speaker/Selected View */}
        <div className="intv-main-speaker-area">
          {mainTrack ? (
            <ParticipantTileWithControls
              track={mainTrack}
              isMainSpeaker={true}
              onMaximize={onMaximize}
              isMaximized={maximizedTrack?.participant.sid === mainTrack.participant.sid}
            />
          ) : (
            <div className="intv-empty-state">
              <div className="intv-empty-icon">🎥</div>
              <p>Waiting for participants to join...</p>
            </div>
          )}
        </div>

        {/* Participants Panel Toggle */}
        {/* <button 
          className="intv-participants-toggle"
          onClick={() => setShowParticipantsPanel(prev => !prev)}
          title="Show all participants"
        >
          <FaUsers />
          <span>{cameraTracks.length}</span>
        </button> */}
      </div>

      {/* FIX: Right Sidebar - Only render when there are participants to show */}
      {showSidebar && (
        <div className="intv-sidebar">
          {sidebarTracks.map((track) => (
            <div
              key={track.participant.sid}
              className={`intv-sidebar-tile ${maximizedTrack?.participant.sid === track.participant.sid ? 'selected' : ''}`}
              onClick={() => onMaximize(track)}
            >
              <ParticipantTileWithControls
                track={track}
                isMainSpeaker={false}
                onMaximize={onMaximize}
                isMaximized={false}
              />
            </div>
          ))}

          {/* Show More button */}
          {remainingCount > 0 && (
            <button
              className="intv-sidebar-tile"
              // className="intv-show-more-btn"
              onClick={() => setShowParticipantsPanel(true)}
            >
              <FaUsers />
              <span>+{remainingCount} More</span>
            </button>
          )}
        </div>
      )}

      {/* Participants Panel */}
      {showParticipantsPanel && (
        <div className="intv-participants-panel">
          <div className="intv-participants-panel-header">
            <h3>All Participants ({cameraTracks.length})</h3>
            <button
              className="intv-participants-panel-close"
              onClick={() => setShowParticipantsPanel(false)}
            >
              <FaTimes />
            </button>
          </div>

          <div className="intv-participants-grid">
            {paginatedTracks.map((track) => (
              <div
                key={track.participant.sid}
                className={`intv-participant-grid-item ${maximizedTrack?.participant.sid === track.participant.sid ? 'selected' : ''}`}
                onClick={() => {
                  onMaximize(track);
                  setShowParticipantsPanel(false);
                }}
              >
                <ParticipantTileWithControls
                  track={track}
                  isMainSpeaker={false}
                  onMaximize={onMaximize}
                  isMaximized={false}
                />
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="intv-pagination">
              <button
                className="intv-page-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <FaChevronLeft />
              </button>
              <span className="intv-page-info">
                {currentPage} / {totalPages}
              </span>
              <button
                className="intv-page-btn"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Custom Controls Component
const IntvControls = ({
  isAdmin,
  onToggleMinimize,
  handleEndMeeting,
  isEnding,
  setShowAdminPanel,
  showAdminPanel
}) => {
  const localParticipant = useLocalParticipant();
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!localParticipant.localParticipant || initialized) return;
    const participant = localParticipant.localParticipant;
    const micTrack = participant.getTrackPublication('microphone');
    const cameraTrack = participant.getTrackPublication('camera');
    if (micTrack) setIsMuted(micTrack.isMuted);
    if (cameraTrack) setIsVideoOff(cameraTrack.isMuted);
    setInitialized(true);
  }, [localParticipant.localParticipant, initialized]);

  useEffect(() => {
    if (!localParticipant.localParticipant) return;
    const handleTrackMuted = (pub) => {
      if (pub.kind === 'audio') setIsMuted(true);
      else if (pub.kind === 'video') setIsVideoOff(true);
    };
    const handleTrackUnmuted = (pub) => {
      if (pub.kind === 'audio') setIsMuted(false);
      else if (pub.kind === 'video') setIsVideoOff(false);
    };
    const participant = localParticipant.localParticipant;
    participant.on('trackMuted', handleTrackMuted);
    participant.on('trackUnmuted', handleTrackUnmuted);
    return () => {
      participant.off('trackMuted', handleTrackMuted);
      participant.off('trackUnmuted', handleTrackUnmuted);
    };
  }, [localParticipant.localParticipant]);

  const toggleMute = async () => {
    if (localParticipant.localParticipant) {
      try {
        await localParticipant.localParticipant.setMicrophoneEnabled(!!isMuted);
      } catch (error) {
        console.error('Failed to toggle microphone:', error);
        toast.error('Failed to toggle microphone');
      }
    }
  };

  const toggleVideo = async () => {
    if (localParticipant.localParticipant) {
      try {
        await localParticipant.localParticipant.setCameraEnabled(!!isVideoOff);
      } catch (error) {
        console.error('Failed to toggle camera:', error);
        toast.error('Failed to toggle camera');
      }
    }
  };

  return (
    <div className="intv-controls-bar">
      <div className="intv-controls-left">
        {isAdmin && (
          <button
            className={`intv-control-btn intv-admin-btn ${showAdminPanel ? 'active' : ''}`}
            onClick={() => setShowAdminPanel((prev) => !prev)}
            type="button"
          >
            <HiOutlineClipboardList />
            <span>Candidates</span>
          </button>
        )}
      </div>

      <div className="intv-controls-center">
        <button
          className={`intv-control-btn ${isMuted ? 'active' : ''}`}
          onClick={toggleMute}
          type="button"
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
          <span>{isMuted ? 'Unmute' : 'Mute'}</span>
        </button>

        <button
          className={`intv-control-btn ${isVideoOff ? 'active' : ''}`}
          onClick={toggleVideo}
          type="button"
          title={isVideoOff ? 'Start Video' : 'Stop Video'}
        >
          {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
          <span>{isVideoOff ? 'Start' : 'Stop'}</span>
        </button>

        <button
          className="intv-control-btn intv-end-btn"
          onClick={handleEndMeeting}
          disabled={isEnding}
          type="button"
        >
          <MdCallEnd />
          <span>{isEnding ? 'Ending...' : 'End'}</span>
        </button>
      </div>

      <div className="intv-controls-right">
        {onToggleMinimize && (
          <button
            className="intv-control-btn intv-minimize-btn"
            onClick={onToggleMinimize}
            type="button"
            title="Minimize"
          >
            <FaWindowMinimize />
          </button>
        )}
      </div>
    </div>
  );
};

// Admin Candidate Progress Popup (same as before)
const IntvAdminPopup = ({ show, onClose, remoteParticipants, candidateProgress, totalQuestions }) => {
  if (!show) return null;

  const totalCandidates = remoteParticipants.length;
  const completedCandidates = remoteParticipants.filter(p => {
    const progress = candidateProgress[p.candidateId || p.userId] || {};
    return progress.completed;
  }).length;

  return (
    <div className="intv-popup-overlay" onClick={onClose}>
      <div className="intv-popup-container" onClick={(e) => e.stopPropagation()}>
        <div className="intv-popup-header">
          <div className="intv-popup-title">
            <FaUserCheck className="intv-popup-icon" />
            <h3>Candidate Progress</h3>
          </div>
          <button className="intv-popup-close" onClick={onClose} type="button">
            <FaTimes />
          </button>
        </div>

        <div className="intv-popup-stats">
          <div className="intv-stat-card">
            <FaUserClock className="intv-stat-icon" />
            <div className="intv-stat-info">
              <span className="intv-stat-value">{totalCandidates}</span>
              <span className="intv-stat-label">Total</span>
            </div>
          </div>
          <div className="intv-stat-card">
            <FaCheckCircle className="intv-stat-icon completed" />
            <div className="intv-stat-info">
              <span className="intv-stat-value">{completedCandidates}</span>
              <span className="intv-stat-label">Completed</span>
            </div>
          </div>
          <div className="intv-stat-card">
            <FaSpinner className="intv-stat-icon in-progress" />
            <div className="intv-stat-info">
              <span className="intv-stat-value">{totalCandidates - completedCandidates}</span>
              <span className="intv-stat-label">In Progress</span>
            </div>
          </div>
        </div>

        <div className="intv-popup-list">
          {remoteParticipants.length === 0 ? (
            <div className="intv-empty-list">
              <FaUserClock className="intv-empty-icon" />
              <p>No candidates have joined yet</p>
              <span>Waiting for candidates to connect...</span>
            </div>
          ) : (
            remoteParticipants.map((participant) => {
              const progress = candidateProgress[participant.candidateId || participant.userId] || {};
              const answered = progress.answeredCount || 0;
              const total = progress.totalQuestions || totalQuestions || 0;
              const percentage = total > 0 ? Math.round((answered / total) * 100) : 0;
              const isCompleted = progress.completed;

              return (
                <div key={participant.socketId || participant.userId} className={`intv-candidate-card ${isCompleted ? 'completed' : ''}`}>
                  <div className="intv-candidate-header">
                    <div className="intv-candidate-avatar">
                      {isCompleted ? <FaCheckCircle /> : <FaUserClock />}
                    </div>
                    <div className="intv-candidate-info">
                      <span className="intv-candidate-name">
                        {participant.displayName || `Candidate ${participant.userId}`}
                      </span>
                      <span className={`intv-candidate-status ${isCompleted ? 'completed' : 'active'}`}>
                        {isCompleted ? 'Completed' : 'In Progress'}
                      </span>
                    </div>
                    <div className="intv-candidate-percentage">
                      {percentage}%
                    </div>
                  </div>
                  <div className="intv-progress-bar">
                    <div
                      className={`intv-progress-fill ${isCompleted ? 'completed' : ''}`}
                      style={{ width: `${percentage}%` }}
                    >
                      {percentage > 15 && <span>{answered}/{total}</span>}
                    </div>
                    {percentage <= 15 && (
                      <span className="intv-progress-text-outside">{answered}/{total}</span>
                    )}
                  </div>
                  {isCompleted && (
                    <div className="intv-completed-badge">
                      <FaCheckCircle /> All questions answered
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// Question Panel Component (same as before)
const IntvQuestionPanel = ({ show, question, index, total, onSubmit, onSkip, isSubmitting }) => {
  if (!show || !question) return null;

  return (
    <div className="intv-question-panel">
      <div className="intv-question-header">
        <div className="intv-question-progress">
          Question {index} of {total}
        </div>
        <div className="intv-question-category">
          {question.category || "Interview"}
        </div>
      </div>
      <div className="intv-question-text">
        {question.text}
      </div>
      <div className="intv-question-actions">
        <button className="intv-skip-btn" onClick={onSkip} disabled={isSubmitting}>Skip</button>
        <button className="intv-submit-btn" onClick={onSubmit}>Submit Answer</button>
      </div>
    </div>
  );
};

// Waiting Panel Component (same as before)
const IntvWaitingPanel = ({ timeRemaining, answeredCount, totalQuestions }) => {
  return (
    <div className="intv-waiting-panel">
      <h3>Next question in {timeRemaining}s</h3>
      <p>Question {answeredCount + 1} of {totalQuestions}</p>
    </div>
  );
};

// Completion Panel Component (same as before)
const IntvCompletionPanel = ({ onClose, baselineStatus }) => {
  return (
    <div className="intv-completion-panel">
      <div className="intv-completion-icon">🎉</div>
      <h3>Interview Completed!</h3>
      <p>Great job! You have answered all interview questions.</p>

      {baselineStatus === "generating" && (
        <p>Creating your baseline profile...</p>
      )}

      {baselineStatus === "completed" && (
        <p>Baseline profile created successfully.</p>
      )}

      {baselineStatus === "failed" && (
        <p>Interview saved. Baseline will be generated later.</p>
      )}

      <button className="intv-completion-btn" onClick={onClose}>
        Close
      </button>
    </div>
  );
};

const InterviewVideoCall = forwardRef(
  (
    {
      roomId,
      userId,
      token,
      interviewId,
      user,
      isAdmin = false,
      role = "admin",
      candidateId,
      onMeetingEnd,
      questions = [],
      participants = [],
      teamName = "",
      isMinimized = false,
      onToggleMinimize,
      teamId,
    },
    ref
  ) => {
    const [livekitToken, setLivekitToken] = useState(null);
    const [livekitUrl, setLivekitUrl] = useState(null);
    const [connected, setConnected] = useState(false);
    const [isEnding, setIsEnding] = useState(false);
    const [hasEnded, setHasEnded] = useState(false);
    const [remoteParticipants, setRemoteParticipants] = useState([]);
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const [candidateProgress, setCandidateProgress] = useState({});
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [showQuestionPanel, setShowQuestionPanel] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [answeredCount, setAnsweredCount] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(questions.length || 0);
    const [questionsStatus, setQuestionsStatus] = useState(
      questions.length > 0 ? "ready" : "generating"
    );
    const [interviewCompleted, setInterviewCompleted] = useState(false);
    const [waitingForNext, setWaitingForNext] = useState(false);
    const [nextQuestionTime, setNextQuestionTime] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [maximizedTrack, setMaximizedTrack] = useState(null);

    const [baselineStatus, setBaselineStatus] = useState(null);

    const socketRef = useRef(null);
    const questionTimerRef = useRef(null);
    const countdownRef = useRef(null);
    const isFetchingRef = useRef(false);
    const initialFetchDoneRef = useRef(false);
    const isEndingRef = useRef(false);
    const hasEndedRef = useRef(false);
    const analyticsSavedRef = useRef(false);

    // Analytics refs
    const analyticsVideoRef = useRef(null);
    const analyticsStreamRef = useRef(null);

    // Face Mesh refs
    const faceMeshRef = useRef(null);
    const cameraRef = useRef(null);
    const faceMeshInitializedRef = useRef(false);
    const faceDetectedRef = useRef(false);

    // Audio refs
    const audioContextRef = useRef(null);
    const audioAnalyserRef = useRef(null);
    const audioSourceRef = useRef(null);
    const audioAnalysisFrameRef = useRef(null);
    const audioInitializedRef = useRef(false);
    const audioDetectedRef = useRef(false);

    // Tracking refs
    const lastBlinkTimeRef = useRef(null);
    const blinkCountRef = useRef(0);
    const lastFrameTimeRef = useRef(Date.now());
    const lastSpeechTimeRef = useRef(Date.now());
    const speechCountRef = useRef(0);
    const pauseStartTimeRef = useRef(null);
    const pitchHistoryRef = useRef([]);
    const lastInterruptTimeRef = useRef(null);
    const lastCameraMinRef = useRef(null);
    const lastVoiceMinRef = useRef(null);
    const collectionIntervalRef = useRef(null);

    // Local video/stream refs
    const localVideoRef = useRef();
    const localStreamRef = useRef();

    // LiveKit room ref for cleanup
    const livekitRoomRef = useRef(null);

    // Camera metrics history
    const camHist = useRef({
      eye_contact_duration: [], blink_rate: [], facial_tension: [], smile_authenticity: [],
      head_tilt_openness: [], lean_posture: [], micro_joy: [], micro_fear: [], micro_disgust: [], micro_surprise: [],
    });

    // Voice metrics history
    const voiceHist = useRef({
      pitch_variability: [], speech_rate: [], pauses: [], vocal_tremor: [], warmth_resonance: [], interrupt_patterns: [],
    });

    // Current camera metrics
    const camCurrent = useRef({
      eye_contact_duration: 0.5, blink_rate: 0, facial_tension: 0.5, smile_authenticity: 0.5,
      head_tilt_openness: 0.5, lean_posture: 0.5, micro_joy: 0.5, micro_fear: 0.1, micro_disgust: 0.05, micro_surprise: 0.2,
    });

    // Current voice metrics
    const voiceCurrent = useRef({
      pitch_variability: 0.5, speech_rate: 0, pauses: 0, vocal_tremor: 0.1, warmth_resonance: 0.5, interrupt_patterns: 0,
    });

    const livekitRoomName = `interview_${interviewId || roomId}`;

    useEffect(() => { hasEndedRef.current = hasEnded; }, [hasEnded]);

    // Handle maximize toggle
    const handleMaximize = useCallback((track) => {
      setMaximizedTrack(prev =>
        prev?.participant.sid === track.participant.sid ? null : track
      );
    }, []);
    const generateBaseline = useCallback(async () => {
      if (isAdmin || !candidateId || !interviewId) return;

      try {
        setBaselineStatus("generating");

        const { data } = await axios.post(
          `${config.baseURL}/api/teams/interview/${interviewId}/baseline/${candidateId}/generate`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (data.success) {
          setBaselineStatus("completed");
          console.log("Baseline generated:", data.baseline);
        } else {
          setBaselineStatus("failed");
        }
      } catch (error) {
        console.error("Baseline generation error:", error);
        setBaselineStatus("failed");
      }
    }, [isAdmin, candidateId, interviewId, token]);
    // ==================== SAVE ANALYTICS FUNCTION ====================
    const saveAnalytics = useCallback(async () => {
      if (isAdmin || !candidateId || analyticsSavedRef.current) return;

      // Add final data points
      addCameraDataPoint();
      addVoiceDataPoint();

      // Check if we have any data to save
      if (camHist.current.eye_contact_duration.length === 0 &&
        voiceHist.current.pitch_variability.length === 0) {
        console.log('No analytics data to save');
        return;
      }

      const payload = buildAnalyticsPayload();

      try {
        const { data } = await axios.post(
          `${config.baseURL}/api/teams/interview/${interviewId}/analytics/save`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (data.success) {
          analyticsSavedRef.current = true;
          console.log("Analytics saved successfully for candidate:", candidateId);

          await generateBaseline();
        }
      } catch (error) {
        console.error("Analytics save error:", error);
      }
    }, [isAdmin, candidateId, interviewId, token, generateBaseline]);

    // ==================== ANALYTICS HELPER FUNCTIONS ====================
    const addCameraDataPoint = useCallback(() => {
      if (isAdmin || !faceDetectedRef.current) return;
      const ts = getCurrentMinute();
      if (lastCameraMinRef.current === ts) return;
      lastCameraMinRef.current = ts;
      Object.keys(camHist.current).forEach(k => {
        let v = camCurrent.current[k];
        if (typeof v !== 'number') v = 0.5;
        if (k === 'blink_rate') v = Math.min(30, Math.max(0, v));
        camHist.current[k].push({ ts, value: v });
      });
    }, [isAdmin]);

    const addVoiceDataPoint = useCallback(() => {
      if (isAdmin || !audioDetectedRef.current) return;
      const ts = getCurrentMinute();
      if (lastVoiceMinRef.current === ts) return;
      lastVoiceMinRef.current = ts;
      Object.keys(voiceHist.current).forEach(k => {
        let v = voiceCurrent.current[k];
        if (typeof v !== 'number') v = 0;
        voiceHist.current[k].push({ ts, value: v });
      });
    }, [isAdmin]);

    const calcAvg = useCallback((arr) => {
      if (!arr?.length) return 0;
      return arr.reduce((sum, item) => sum + item.value, 0) / arr.length;
    }, []);

    const buildAnalyticsPayload = useCallback(() => {
      return {
        candidateId,
        cameraMetrics: camHist.current,
        voiceMetrics: voiceHist.current,
        summary: {
          avgEyeContact: calcAvg(camHist.current.eye_contact_duration),
          avgBlinkRate: calcAvg(camHist.current.blink_rate),
          avgFacialTension: calcAvg(camHist.current.facial_tension),
          avgSmileAuthenticity: calcAvg(camHist.current.smile_authenticity),
          avgHeadTiltOpenness: calcAvg(camHist.current.head_tilt_openness),
          avgPitchVariability: calcAvg(voiceHist.current.pitch_variability),
          avgSpeechRate: calcAvg(voiceHist.current.speech_rate),
          avgPauses: calcAvg(voiceHist.current.pauses),
          avgVocalTremor: calcAvg(voiceHist.current.vocal_tremor),
          avgWarmthResonance: calcAvg(voiceHist.current.warmth_resonance),
          totalInterruptions: voiceHist.current.interrupt_patterns.filter((item) => item.value === 1).length,
        },
      };
    }, [candidateId, calcAvg]);

    // ==================== FACE MESH ANALYTICS ====================
    const processFaceAnalytics = useCallback((landmarks) => {
      if (isAdmin) return;
      const now = Date.now();
      if (!faceDetectedRef.current) faceDetectedRef.current = true;

      const lm = landmarks;

      // Eye contact
      const li = lm[133], ri = lm[362];
      if (li && ri) camCurrent.current.eye_contact_duration =
        Math.min(0.95, Math.max(0.3, 0.7 + Math.abs(li.x - ri.x) * 0.3));

      // Blink rate
      const elapsed = (now - lastFrameTimeRef.current) / 1000;
      if (elapsed >= 1) {
        camCurrent.current.blink_rate = Math.min(25, Math.max(8, (blinkCountRef.current / elapsed) * 60));
        blinkCountRef.current = 0;
        lastFrameTimeRef.current = now;
      }

      // Facial tension
      const bl = lm[70], br = lm[300];
      if (bl && br) camCurrent.current.facial_tension =
        Math.min(0.9, Math.max(0.1, Math.abs(bl.y - br.y) * 10));

      // Smile
      const ml = lm[61], mr = lm[291], mt = lm[13], mb = lm[14];
      if (ml && mr && mt && mb) {
        const w = Math.abs(ml.x - mr.x), h = Math.abs(mt.y - mb.y);
        camCurrent.current.smile_authenticity = Math.min(0.95, Math.max(0.3, (h / (w + 0.01)) * 3));
      }

      // Head tilt
      const nose = lm[1], le = lm[33], re = lm[263];
      if (nose && le && re) {
        const ey = (le.y + re.y) / 2;
        camCurrent.current.head_tilt_openness = Math.min(0.9, Math.max(0.3, 0.6 + (nose.y - ey) * 2));
      }

      // Posture
      const ls = lm[11], rs = lm[12];
      if (ls && rs) {
        const sw = Math.abs(ls.x - rs.x);
        camCurrent.current.lean_posture = Math.max(0.4, Math.min(0.9, 1 - Math.min(1, sw) + 0.4));
      }

      // Micro-expressions
      if (ml && mr) camCurrent.current.micro_joy = Math.min(0.95, Math.max(0.3, Math.abs(ml.x - mr.x) * 2));
      if (bl && br) camCurrent.current.micro_fear = Math.min(0.9, Math.max(0.05, Math.abs(bl.y - br.y) * 5));

      // Blink detection
      const lt = lm[159], lb = lm[145], rt2 = lm[386], rb2 = lm[374];
      if (lt && lb && rt2 && rb2) {
        const lo = Math.abs(lt.y - lb.y), ro = Math.abs(rt2.y - rb2.y);
        if ((lo < 0.01 || ro < 0.01) && (now - (lastBlinkTimeRef.current || 0)) > 200) {
          blinkCountRef.current++;
          lastBlinkTimeRef.current = now;
        }
      }
    }, [isAdmin]);

    const stopFaceMesh = useCallback(async () => {
      if (cameraRef.current) { try { await cameraRef.current.stop(); } catch (_) { } cameraRef.current = null; }
      if (faceMeshRef.current) { try { await faceMeshRef.current.close(); } catch (_) { } faceMeshRef.current = null; }
      faceMeshInitializedRef.current = false;
      faceDetectedRef.current = false;
    }, []);

    const initFaceMesh = useCallback(async (videoOff = false) => {
      if (isAdmin || videoOff || faceMeshInitializedRef.current || !localVideoRef.current || !localStreamRef.current) return;
      try {
        await stopFaceMesh();
        faceMeshRef.current = new FaceMesh({
          locateFile: f => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${f}`,
        });
        faceMeshRef.current.setOptions({ maxNumFaces: 1, refineLandmarks: true, minDetectionConfidence: 0.5, minTrackingConfidence: 0.5 });
        faceMeshRef.current.onResults(results => {
          if (isAdmin) return;
          if (results.multiFaceLandmarks?.length > 0 && !isMinimized) {
            processFaceAnalytics(results.multiFaceLandmarks[0]);
          } else {
            faceDetectedRef.current = false;
          }
        });
        cameraRef.current = new Camera(localVideoRef.current, {
          onFrame: async () => {
            if (isAdmin || !faceMeshRef.current || !localVideoRef.current || isMinimized) return;
            try { await faceMeshRef.current.send({ image: localVideoRef.current }); } catch (_) { }
          },
          width: 640, height: 480,
        });
        await cameraRef.current.start();
        faceMeshInitializedRef.current = true;
      } catch (err) {
        console.warn('FaceMesh init error:', err.message);
      }
    }, [isAdmin, isMinimized, processFaceAnalytics, stopFaceMesh]);

    // ==================== AUDIO ANALYTICS ====================
    const startAudioAnalysis = useCallback(() => {
      if (!audioAnalyserRef.current || audioContextRef.current?.state !== 'running') return;
      const freqArr = new Uint8Array(audioAnalyserRef.current.frequencyBinCount);
      const timeArr = new Uint8Array(audioAnalyserRef.current.fftSize);
      let lastVoiceActivity = false;

      const analyze = () => {
        if (!audioAnalyserRef.current) return;
        try {
          audioAnalyserRef.current.getByteFrequencyData(freqArr);
          audioAnalyserRef.current.getByteTimeDomainData(timeArr);

          const energy = freqArr.reduce((s, v) => s + v, 0) / freqArr.length / 255;
          const speaking = energy > 0.1;

          if (!audioDetectedRef.current && energy > 0.05) audioDetectedRef.current = true;

          if (speaking !== lastVoiceActivity) {
            if (speaking) {
              const now = Date.now();
              voiceCurrent.current.interrupt_patterns =
                lastInterruptTimeRef.current && (now - lastInterruptTimeRef.current) < 500 ? 1 : 0;
              lastInterruptTimeRef.current = now;
            }
            lastVoiceActivity = speaking;
          }

          // Pitch detection
          let maxCorr = 0, pitch = 120;
          for (let lag = 20; lag < Math.min(1000, timeArr.length - 100); lag++) {
            let corr = 0;
            for (let i = 0; i < timeArr.length - lag; i++) corr += (timeArr[i] - 128) * (timeArr[i + lag] - 128);
            corr /= (timeArr.length - lag);
            if (corr > maxCorr && corr > 0) {
              const p = audioContextRef.current.sampleRate / lag;
              if (p > 80 && p < 300) { maxCorr = corr; pitch = p; }
            }
          }
          pitch = Math.min(250, Math.max(85, pitch));
          pitchHistoryRef.current.push(pitch);
          if (pitchHistoryRef.current.length > 100) pitchHistoryRef.current.shift();

          if (pitchHistoryRef.current.length >= 2) {
            const recent = pitchHistoryRef.current.slice(-10);
            const mean = recent.reduce((a, b) => a + b, 0) / recent.length;
            const variance = recent.reduce((a, b) => a + (b - mean) ** 2, 0) / recent.length;
            voiceCurrent.current.pitch_variability = Math.min(0.9, Math.sqrt(variance) / 50);
          }

          const now = Date.now();
          if (speaking) speechCountRef.current++;
          const elapsed = (now - lastSpeechTimeRef.current) / 1000;
          if (elapsed >= 1) {
            voiceCurrent.current.speech_rate = Math.min(180, Math.max(80, (speechCountRef.current / elapsed) * 60));
            speechCountRef.current = 0;
            lastSpeechTimeRef.current = now;
          }

          if (speaking) { pauseStartTimeRef.current = null; }
          else if (!pauseStartTimeRef.current) pauseStartTimeRef.current = now;
          voiceCurrent.current.pauses = pauseStartTimeRef.current
            ? Math.min(0.4, (now - pauseStartTimeRef.current) / 1000 / 15)
            : 0.15;

          if (pitchHistoryRef.current.length >= 10) {
            const r = pitchHistoryRef.current.slice(-10);
            const m = r.reduce((a, b) => a + b, 0) / r.length;
            voiceCurrent.current.vocal_tremor = Math.min(0.3, r.reduce((a, b) => a + Math.abs(b - m), 0) / r.length / 80);
          }

          const low = freqArr.slice(0, 100).reduce((a, b) => a + b, 0);
          const total = freqArr.reduce((a, b) => a + b, 0);
          voiceCurrent.current.warmth_resonance = Math.min(0.85, Math.max(0.4, low / (total || 1)));

        } catch (_) { }
        audioAnalysisFrameRef.current = requestAnimationFrame(analyze);
      };
      analyze();
    }, []);

    const stopAudioAnalytics = useCallback(() => {
      if (audioAnalysisFrameRef.current) { cancelAnimationFrame(audioAnalysisFrameRef.current); audioAnalysisFrameRef.current = null; }
      try { audioSourceRef.current?.disconnect(); } catch (_) { }
      audioSourceRef.current = null;
      audioAnalyserRef.current = null;
      try { audioContextRef.current?.close(); } catch (_) { }
      audioContextRef.current = null;
      audioInitializedRef.current = false;
      audioDetectedRef.current = false;
    }, []);

    const stopAnalytics = useCallback(() => {
      stopFaceMesh();
      stopAudioAnalytics();
    }, [stopFaceMesh, stopAudioAnalytics]);

    const startAnalyticsStream = useCallback(async () => {
      if (isAdmin) return;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 30 } },
          audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 }
        });

        analyticsStreamRef.current = stream;

        if (analyticsVideoRef.current) {
          analyticsVideoRef.current.srcObject = stream;

          await new Promise((resolve) => {
            if (analyticsVideoRef.current.readyState >= 2) resolve();
            else analyticsVideoRef.current.onloadeddata = resolve;
          });

          localVideoRef.current = analyticsVideoRef.current;
          localStreamRef.current = stream;

          await initFaceMesh();
        }

        // Initialize audio analytics
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        audioAnalyserRef.current = audioContextRef.current.createAnalyser();
        audioAnalyserRef.current.fftSize = 2048;
        audioSourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
        audioSourceRef.current.connect(audioAnalyserRef.current);

        const resume = async () => {
          if (audioContextRef.current?.state === 'suspended') {
            await audioContextRef.current.resume();
            audioInitializedRef.current = true;
            startAudioAnalysis();
            document.removeEventListener('click', resume);
            document.removeEventListener('touchstart', resume);
          }
        };
        document.addEventListener('click', resume);
        document.addEventListener('touchstart', resume);
        if (audioContextRef.current.state === 'running') {
          audioInitializedRef.current = true;
          startAudioAnalysis();
        }

        console.log('Analytics stream started successfully');
      } catch (error) {
        console.error('Failed to start analytics stream:', error);
      }
    }, [isAdmin, initFaceMesh, startAudioAnalysis]);

    // ==================== CLEANUP FUNCTION ====================
    const cleanupMedia = useCallback(async () => {
      hasEndedRef.current = true;
      isEndingRef.current = true;

      // Clear all timers
      if (questionTimerRef.current) { clearTimeout(questionTimerRef.current); questionTimerRef.current = null; }
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
      if (collectionIntervalRef.current) { clearInterval(collectionIntervalRef.current); collectionIntervalRef.current = null; }

      // Save analytics before stopping
      if (!isAdmin && !analyticsSavedRef.current) {
        try {
          await saveAnalytics();
        } catch (err) {
          console.error('Error saving analytics during cleanup:', err);
        }
      }

      // Stop analytics
      stopAnalytics();

      // Stop analytics stream tracks
      if (analyticsStreamRef.current) {
        analyticsStreamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log('Stopped analytics track:', track.kind);
        });
        analyticsStreamRef.current = null;
      }

      // IMPORTANT: Stop LiveKit room tracks for candidates
      if (!isAdmin && livekitRoomRef.current) {
        try {
          const localParticipant = livekitRoomRef.current.localParticipant;
          if (localParticipant) {
            // Disable camera and microphone first
            await localParticipant.setCameraEnabled(false);
            await localParticipant.setMicrophoneEnabled(false);

            // Stop all published tracks
            const publications = localParticipant.getTrackPublications();
            publications.forEach((publication) => {
              if (publication && publication.track) {
                publication.track.stop();
                console.log('Stopped LiveKit track:', publication.kind);
              }
            });
          }

          // Disconnect from LiveKit room
          await livekitRoomRef.current.disconnect();
          console.log('Disconnected from LiveKit room');
        } catch (error) {
          console.error('Error cleaning up LiveKit tracks:', error);
        }
      }

      // Reset UI states
      setShowQuestionPanel(false);
      setWaitingForNext(false);
      setCurrentQuestion(null);
      setInterviewCompleted(true);
      setHasEnded(true);
      setConnected(false);

      // Disconnect socket
      if (socketRef.current) {
        socketRef.current.emit("leave-interview", { interviewId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    }, [isAdmin, saveAnalytics, stopAnalytics, interviewId]);
    const fetchNextQuestion = useCallback(async () => {
      if (!interviewId || !candidateId || isFetchingRef.current) return;

      isFetchingRef.current = true;

      try {
        const { data } = await axios.get(
          `${config.baseURL}/api/teams/interview/${interviewId}/next-question`,
          {
            params: { candidateId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (data.success) {
          if (data.questions_status === "generating") {
            setQuestionsStatus("generating");
            setShowQuestionPanel(false);
            setCurrentQuestion(null);
            return;
          }

          setQuestionsStatus("ready");

          if (data.completed) {
            setInterviewCompleted(true);
            setShowQuestionPanel(false);
            toast.success("You have completed all questions!");
          } else if (data.question) {
            setCurrentQuestion(data.question);
            setQuestionIndex((prev) => prev + 1);
            setShowQuestionPanel(true);
            setWaitingForNext(false);

            if (data.progress?.total) {
              setTotalQuestions(data.progress.total);
            }

            if (questionTimerRef.current) {
              clearTimeout(questionTimerRef.current);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load next question:", error);
        toast.error("Failed to load next question");
      } finally {
        isFetchingRef.current = false;
      }
    }, [interviewId, candidateId, token]);
    // ==================== SOCKET CONNECTION ====================
    useEffect(() => {
      if (!interviewId || !userId || !token || hasEnded) return;

      const socket = io(config.baseURL, { auth: { token }, transports: ["websocket"] });
      socketRef.current = socket;

      socket.on("connect", () => {
        setConnected(true);
        socket.emit("join-interview", { interviewId, role, candidateId: candidateId || null });
      });

      socket.on("participants-update", (list) => {
        setRemoteParticipants(list.filter((p) => String(p.userId) !== String(userId)));
      });

      socket.on("candidate-progress", (payload) => {
        const { candidateId: progressCandidateId, answeredCount, totalQuestions, completed } = payload;
        setCandidateProgress((prev) => ({ ...prev, [progressCandidateId]: { answeredCount, totalQuestions, completed } }));
      });
      socket.on("interview-questions-ready", (payload) => {
        console.log("✅ Questions ready:", payload);

        if (String(payload.interview_id) !== String(interviewId)) return;

        const eventCandidateId = payload.candidateId || payload.candidate_id;

        if (!isAdmin && String(eventCandidateId) === String(candidateId)) {
          setQuestionsStatus("ready");
          setTotalQuestions(payload.total_questions || payload.questions?.length || 25);

          if (!initialFetchDoneRef.current) {
            initialFetchDoneRef.current = true;

            setTimeout(() => {
              fetchNextQuestion();
            }, 1000);
          }

          toast.success("Interview questions are ready");
        }

        if (isAdmin) {
          setCandidateProgress((prev) => ({
            ...prev,
            [eventCandidateId]: {
              ...(prev[eventCandidateId] || {}),
              totalQuestions: payload.total_questions || 25,
            },
          }));
        }
      });
      socket.on("request-analytics-save", async () => {
        if (!isAdmin && !hasEndedRef.current) {
          try {
            await saveAnalytics();
            socket.emit("analytics-save-confirmed", { interviewId, candidateId });
          } catch (err) {
            console.error('Error saving analytics on request:', err);
          }
        }
      });

      socket.on("interview-ended", async (data) => {
        console.log("Interview ended event received in VideoCall:", data);

        if (hasEndedRef.current || isEndingRef.current) return;

        toast.info("Interview has been ended by the admin");

        await cleanupMedia();

        if (onMeetingEnd) {
          setTimeout(() => onMeetingEnd(), 100);
        }
      });

      socket.on("disconnect", () => setConnected(false));

      return () => {
        socket.emit("leave-interview", { interviewId });
        socket.disconnect();
        socketRef.current = null;
      };
    }, [
      interviewId,
      userId,
      token,
      role,
      candidateId,
      hasEnded,
      isAdmin,
      fetchNextQuestion,
      saveAnalytics,
      cleanupMedia,
      onMeetingEnd,
    ]);

    // ==================== EFFECTS ====================
    useEffect(() => {
      if (!isAdmin) startAnalyticsStream();
      return () => {
        if (analyticsStreamRef.current) {
          analyticsStreamRef.current.getTracks().forEach(track => track.stop());
          analyticsStreamRef.current = null;
        }
        stopAnalytics();
      };
    }, [isAdmin, startAnalyticsStream, stopAnalytics]);

    useEffect(() => {
      if (isAdmin) return;
      collectionIntervalRef.current = setInterval(() => {
        addCameraDataPoint();
        addVoiceDataPoint();
      }, 60000);
      return () => {
        if (collectionIntervalRef.current) clearInterval(collectionIntervalRef.current);
      };
    }, [isAdmin, addCameraDataPoint, addVoiceDataPoint]);

    // Fetch LiveKit token
    useEffect(() => {
      const fetchToken = async () => {
        try {
          const participantName = user?.first_name || user?.name || user?.username || `User ${userId}`;
          const { data } = await axios.post(
            `${config.baseURL}/api/livekit/token`,
            { roomName: livekitRoomName, participantName, metadata: { userId, role, candidateId: candidateId || null, interviewId } },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!data.success) { toast.error(data.message || "Failed to join LiveKit room"); return; }
          setLivekitToken(data.token);
          setLivekitUrl(data.url);
        } catch (error) {
          console.error("LiveKit token error:", error);
          toast.error("Failed to join video room");
        }
      };
      if (token && userId && interviewId) fetchToken();
    }, [token, userId, interviewId, livekitRoomName, role, candidateId]);

    // ==================== QUESTION HANDLING ====================


    useEffect(() => {
      if (
        !isAdmin &&
        interviewId &&
        candidateId &&
        !interviewCompleted &&
        !initialFetchDoneRef.current
      ) {
        if (questions.length > 0) {
          initialFetchDoneRef.current = true;
          setQuestionsStatus("ready");
          setTotalQuestions(questions.length);

          setTimeout(() => {
            fetchNextQuestion();
          }, 2000);
        } else {
          setQuestionsStatus("generating");
        }
      }
    }, [
      interviewId,
      candidateId,
      isAdmin,
      questions.length,
      interviewCompleted,
      fetchNextQuestion,
    ]);
    useEffect(() => {
      if (
        isAdmin ||
        !interviewId ||
        !candidateId ||
        interviewCompleted ||
        initialFetchDoneRef.current
      ) {
        return;
      }

      const interval = setInterval(async () => {
        try {
          const { data } = await axios.get(
            `${config.baseURL}/api/teams/interview/${interviewId}/next-question`,
            {
              params: { candidateId },
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (data.success && data.question) {
            initialFetchDoneRef.current = true;
            setQuestionsStatus("ready");
            setCurrentQuestion(data.question);
            setQuestionIndex(1);
            setShowQuestionPanel(true);
            setWaitingForNext(false);
            setTotalQuestions(data.progress?.total || 25);
            clearInterval(interval);
          }
        } catch (error) {
          console.error("Question polling error:", error);
        }
      }, 3000);

      return () => clearInterval(interval);
    }, [isAdmin, interviewId, candidateId, interviewCompleted, token]);

    const logCandidateQuestion = useCallback(async (questionId, action) => {
      try {
        const { data } = await axios.post(
          `${config.baseURL}/api/teams/interview/${interviewId}/log-question`,
          { candidateId, question_id: questionId, action },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return data.success;
      } catch (error) {
        console.error("Failed to log question:", error);
        return false;
      }
    }, [interviewId, candidateId, token]);

    const emitCandidateProgress = useCallback((newAnsweredCount, completed) => {
      socketRef.current?.emit("candidate-progress-update", {
        interviewId, candidateId, answeredCount: newAnsweredCount, totalQuestions, completed
      });
    }, [interviewId, candidateId, totalQuestions]);

    const handleSubmitAnswer = useCallback(async () => {
      if (!currentQuestion || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const success = await logCandidateQuestion(currentQuestion.id, "answered");
        if (success) {
          const newAnsweredCount = answeredCount + 1;
          const completed = newAnsweredCount >= totalQuestions;
          setAnsweredCount(newAnsweredCount);
          setShowQuestionPanel(false);
          setCurrentQuestion(null);
          emitCandidateProgress(newAnsweredCount, completed);
          if (completed) {
            setInterviewCompleted(true);
            return;
          }
          setWaitingForNext(true);
          setNextQuestionTime(Date.now() + 60000);
          questionTimerRef.current = setTimeout(() => {
            setWaitingForNext(false);
            fetchNextQuestion();
          }, 60000);
        }
      } finally {
        setIsSubmitting(false);
      }
    }, [currentQuestion, isSubmitting, answeredCount, totalQuestions, logCandidateQuestion, emitCandidateProgress, fetchNextQuestion]);

    const handleSkipQuestion = useCallback(async () => {
      if (!currentQuestion || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const success = await logCandidateQuestion(currentQuestion.id, "skipped");
        if (success) {
          const newAnsweredCount = answeredCount + 1;
          const completed = newAnsweredCount >= totalQuestions;
          setAnsweredCount(newAnsweredCount);
          setShowQuestionPanel(false);
          setCurrentQuestion(null);
          emitCandidateProgress(newAnsweredCount, completed);
          if (completed) {
            setInterviewCompleted(true);
            return;
          }
          setWaitingForNext(true);
          setNextQuestionTime(Date.now() + 60000);
          questionTimerRef.current = setTimeout(() => {
            setWaitingForNext(false);
            fetchNextQuestion();
          }, 60000);
        }
      } finally {
        setIsSubmitting(false);
      }
    }, [currentQuestion, isSubmitting, answeredCount, totalQuestions, logCandidateQuestion, emitCandidateProgress, fetchNextQuestion]);

    useEffect(() => {
      if (!waitingForNext || !nextQuestionTime) return;
      const update = () => setTimeRemaining(Math.max(0, Math.ceil((nextQuestionTime - Date.now()) / 1000)));
      update();
      countdownRef.current = setInterval(update, 1000);
      return () => clearInterval(countdownRef.current);
    }, [waitingForNext, nextQuestionTime]);

    // ==================== END MEETING ====================
    const handleEndMeeting = useCallback(async () => {
      if (isEndingRef.current || hasEndedRef.current) return;
      isEndingRef.current = true;
      setIsEnding(true);

      try {
        if (isAdmin) {
          // Admin ending - emit event to end for everyone
          if (socketRef.current?.connected) {
            socketRef.current.emit('admin-ended-interview', {
              interviewId,
              decision: 'completed'
            });
          }

          // Make API call to end interview on server
          try {
            await axios.post(
              `${config.baseURL}/api/teams/interview/${interviewId}/end`,
              { decision: 'completed', team_id: teamId },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (error) {
            console.error('Error ending interview on server:', error);
          }
        } else {
          // Candidate ending - stop media first, then notify

          // Stop analytics stream immediately
          if (analyticsStreamRef.current) {
            analyticsStreamRef.current.getTracks().forEach(track => {
              track.stop();
              console.log('Candidate stopped analytics track:', track.kind);
            });
            analyticsStreamRef.current = null;
          }

          // Stop LiveKit tracks immediately
          if (livekitRoomRef.current) {
            try {
              const localParticipant = livekitRoomRef.current.localParticipant;
              if (localParticipant) {
                await localParticipant.setCameraEnabled(false);
                await localParticipant.setMicrophoneEnabled(false);

                const publications = localParticipant.getTrackPublications();
                publications.forEach((publication) => {
                  if (publication && publication.track) {
                    publication.track.stop();
                    console.log('Candidate stopped LiveKit track:', publication.kind);
                  }
                });
              }
              await livekitRoomRef.current.disconnect();
              console.log('Candidate disconnected from LiveKit room');
            } catch (error) {
              console.error('Error stopping candidate tracks:', error);
            }
          }

          // Notify server
          if (socketRef.current?.connected) {
            socketRef.current.emit('candidate-left-interview', {
              interviewId,
              candidateId
            });

            // Wait briefly for the server to process the leave event
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        // Clean up remaining resources
        await cleanupMedia();

        // Call the parent's onMeetingEnd callback
        if (onMeetingEnd) {
          setTimeout(() => onMeetingEnd(), 100);
        }
      } catch (error) {
        console.error('Error ending meeting:', error);
        toast.error('Error ending meeting');
      } finally {
        isEndingRef.current = false;
        setIsEnding(false);
      }
    }, [isAdmin, interviewId, teamId, token, candidateId, cleanupMedia, onMeetingEnd]);

    useEffect(() => {
      const handleBeforeUnload = () => {
        // Emergency cleanup when tab/browser is closed
        if (analyticsStreamRef.current) {
          analyticsStreamRef.current.getTracks().forEach(track => track.stop());
        }
        if (livekitRoomRef.current) {
          try {
            const localParticipant = livekitRoomRef.current.localParticipant;
            if (localParticipant) {
              localParticipant.getTrackPublications().forEach(pub => {
                if (pub.track) pub.track.stop();
              });
            }
            livekitRoomRef.current.disconnect();
          } catch (e) {
            console.error('Emergency cleanup error:', e);
          }
        }
        if (socketRef.current?.connected) {
          socketRef.current.emit('candidate-left-interview', {
            interviewId,
            candidateId
          });
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, [interviewId, candidateId]);

    useImperativeHandle(ref, () => ({
      endMeeting: handleEndMeeting,
      getAnalyticsData: () => isAdmin ? null : { cameraMetrics: camHist.current, voiceMetrics: voiceHist.current },
    }));

    // ==================== RENDER ====================
    if (hasEnded) return null;

    if (isMinimized) {
      return (
        <div className="intv-minimized">
          <div className="intv-minimized-header">
            <span className="intv-minimized-title">Interview Call</span>
            <button className="intv-minimized-maximize" onClick={onToggleMinimize} type="button">
              <FaWindowMaximize />
            </button>
          </div>
          <div className="intv-minimized-video">
            <video ref={analyticsVideoRef} autoPlay muted playsInline />
            <div className="intv-minimized-label">You</div>
          </div>
        </div>
      );
    }

    if (!livekitToken || !livekitUrl) {
      return (
        <div className="intv-loading">
          <div className="intv-loading-spinner"></div>
          <h3>Joining video room...</h3>
        </div>
      );
    }

    return (
      <div className="intv-container">
        <video ref={analyticsVideoRef} autoPlay muted playsInline style={{ display: "none" }} />

        <div className="intv-livekit-wrapper">
          <LiveKitRoom
            serverUrl={livekitUrl}
            token={livekitToken}
            connect={true}
            video={true}
            audio={true}
            ref={livekitRoomRef}
            onDisconnected={() => { if (!hasEndedRef.current) handleEndMeeting(); }}
          >
            <EnhancedVideoLayout
              onMaximize={handleMaximize}
              maximizedTrack={maximizedTrack}
              participants={remoteParticipants}
            />
            <RoomAudioRenderer />
            <IntvControls
              isAdmin={isAdmin}
              onToggleMinimize={onToggleMinimize}
              handleEndMeeting={handleEndMeeting}
              isEnding={isEnding}
              setShowAdminPanel={setShowAdminPanel}
              showAdminPanel={showAdminPanel}
            />
          </LiveKitRoom>
        </div>

        <IntvAdminPopup
          show={showAdminPanel}
          onClose={() => setShowAdminPanel(false)}
          remoteParticipants={remoteParticipants}
          candidateProgress={candidateProgress}
          totalQuestions={totalQuestions}
        />

        {!isAdmin && !interviewCompleted && (
          <>
            {questionsStatus === "generating" && !showQuestionPanel && !waitingForNext && (
              <div className="intv-waiting-panel">
                <h3>Generating interview questions...</h3>
                <p>Please wait. Your interview has started.</p>
              </div>
            )}

            <IntvQuestionPanel
              show={showQuestionPanel}
              question={currentQuestion}
              index={questionIndex}
              total={totalQuestions}
              onSubmit={handleSubmitAnswer}
              onSkip={handleSkipQuestion}
              isSubmitting={isSubmitting}
            />

            {waitingForNext && !showQuestionPanel && (
              <IntvWaitingPanel
                timeRemaining={timeRemaining}
                answeredCount={answeredCount}
                totalQuestions={totalQuestions}
              />
            )}
          </>
        )}

        {!isAdmin && interviewCompleted && (
          <IntvCompletionPanel
            onClose={handleEndMeeting}
            baselineStatus={baselineStatus}
          />
        )}
      </div>
    );
  }
);

export default InterviewVideoCall;